import * as mapboxgl from 'mapbox-gl';
import * as turf from '@turf/turf';
import type { TranslateService } from '@ngx-translate/core';
import type { Feature, Point, Polygon } from 'geojson';
import { coordinatesTable } from './map-popup-editors';
import { styleEditorContainer } from './map-editor-ui';

const CELL_STYLE = 'padding:4px 6px;font-variant-numeric:tabular-nums;';

export function coordinatesOf(feature: Feature<Point | Polygon>): [number, number][] {
  if (feature.geometry.type === 'Point') return [feature.geometry.coordinates as [number, number]];
  const ring = (feature.geometry.coordinates[0] ?? []) as [number, number][];
  return ring.length > 1 ? ring.slice(0, -1) : ring;
}

export function createCoordinatesView(
  title: string,
  coordinates: [number, number][],
  translate: Pick<TranslateService, 'instant'>,
  detail?: HTMLElement
): HTMLElement {
  const wrap = document.createElement('div');
  wrap.dataset['testid'] = 'map-coordinates-view';
  styleEditorContainer(wrap, 'min(360px,75vw)');
  wrap.style.maxHeight = 'min(520px,70vh)';
  wrap.setAttribute('aria-label', title);

  const heading = document.createElement('div');
  heading.textContent = title;
  heading.style.cssText = 'font-weight:700;padding-right:20px;flex-shrink:0;';
  wrap.appendChild(heading);
  if (detail) wrap.appendChild(detail);

  const scroller = document.createElement('div');
  scroller.style.cssText = 'max-height:min(360px,45vh);min-height:0;flex:1 1 auto;overflow-y:auto;';
  const { table, tbody } = coordinatesTable(translate, false);
  coordinates.forEach(([lng, lat], index) => {
    const row = tbody.insertRow();
    row.style.borderTop = '1px solid #e5e5e5';
    for (const value of [String(index + 1), lat.toFixed(6), lng.toFixed(6)]) {
      const cell = row.insertCell();
      cell.style.cssText = CELL_STYLE;
      cell.textContent = value;
    }
  });
  scroller.appendChild(table);
  wrap.appendChild(scroller);
  return wrap;
}

export function openCoordinatesView(
  map: mapboxgl.Map,
  feature: Feature<Point | Polygon>,
  title: string,
  translate: Pick<TranslateService, 'instant'>,
  detail?: HTMLElement
): mapboxgl.Popup {
  const [lng, lat] = turf.centroid(feature).geometry.coordinates;
  return new mapboxgl.Popup({ closeButton: true, closeOnClick: false, offset: 16, maxWidth: '400px' })
    .setLngLat([lng, lat])
    .setDOMContent(createCoordinatesView(title, coordinatesOf(feature), translate, detail))
    .addTo(map);
}
